import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import { resolveDark } from "../lib/theme";
import { getTheme, currentSeasonalTheme } from "../lib/themes";

// Appearance = the Themes layer on top of the existing light/dark colour mode. It is COSMETIC ONLY.
// Preferences persist to the user's account (settings.appearance) so they follow the user across
// browsers / PWA, with a localStorage mirror for instant paint before auth resolves.
const AppearanceContext = createContext(null);
export const useAppearance = () => useContext(AppearanceContext);

const LS_KEY = "plannio_appearance_v1";
const DEFAULTS = {
  preferredTheme: "default",
  seasonalSuggestions: true,
  autoSwitch: false,
  planniVisibility: "normal",   // normal | reduced | hidden
  speechBubbles: true,
  celebrations: true,
  ambient: true,                // decorative ambient effects
  reduceMotion: false,
  highContrast: false,
};

const readLS = () => { try { return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(LS_KEY) || "{}")) }; } catch { return { ...DEFAULTS }; } };

function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Paint the resolved theme's accent tokens onto <html> for the current colour mode.
function paintTheme(themeId) {
  const t = getTheme(themeId);
  const dark = resolveDark();
  const p = dark ? (t.dark || t.palette) : t.palette;
  const a2 = t.accent2 || p.accent;
  const root = document.documentElement;
  root.setAttribute("data-theme", themeId);
  root.style.setProperty("--theme-accent", p.accent);
  root.style.setProperty("--theme-accent-soft", p.accentSoft.startsWith("#") ? hexToRgba(p.accent, 0.12) : p.accentSoft);
  root.style.setProperty("--theme-on-accent", p.onAccent);
  root.style.setProperty("--theme-tint-1", p.tint1.startsWith("#") ? hexToRgba(p.accent, dark ? 0.16 : 0.09) : p.tint1);
  root.style.setProperty("--theme-tint-2", p.tint2.startsWith("#") ? hexToRgba(a2, dark ? 0.13 : 0.08) : p.tint2);
  root.style.setProperty("--theme-chip", p.chip);
  root.style.setProperty("--theme-accent-2", a2);
}

export function AppearanceProvider({ children }) {
  const [prefs, setPrefs] = useState(readLS);
  const [previewTheme, setPreviewTheme] = useState(null); // transient, never persisted
  const loadedFromAccount = useRef(false);
  const saveTimer = useRef(null);

  const seasonal = useMemo(() => currentSeasonalTheme(), []);
  const resolvedTheme = previewTheme
    || (prefs.autoSwitch && seasonal ? seasonal.id : prefs.preferredTheme)
    || "default";

  // Apply theme + accessibility classes whenever the resolved appearance changes.
  useEffect(() => {
    paintTheme(resolvedTheme);
    const root = document.documentElement;
    root.classList.toggle("pl-no-ambient", !prefs.ambient);
    root.classList.toggle("pl-reduce-motion", !!prefs.reduceMotion);
    root.classList.toggle("pl-contrast", !!prefs.highContrast);
  }, [resolvedTheme, prefs.ambient, prefs.reduceMotion, prefs.highContrast]);

  // Re-paint accent tokens when the light/dark colour mode flips (class change on <html>).
  useEffect(() => {
    const obs = new MutationObserver(() => paintTheme(resolvedTheme));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, [resolvedTheme]);

  // Merge the account's saved appearance once, the first time it arrives.
  const hydrateFromAccount = useCallback((serverAppearance) => {
    if (loadedFromAccount.current || !serverAppearance) return;
    loadedFromAccount.current = true;
    setPrefs((p) => {
      const merged = { ...p, ...serverAppearance };
      try { localStorage.setItem(LS_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
      return merged;
    });
  }, []);

  const persist = useCallback((next) => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (localStorage.getItem("plannio_token")) api.put("/appearance", next).catch(() => {});
    }, 500);
  }, []);

  const update = useCallback((patch) => {
    setPrefs((p) => { const next = { ...p, ...patch }; persist(next); return next; });
  }, [persist]);

  const applyTheme = useCallback((themeId) => { setPreviewTheme(null); update({ preferredTheme: themeId }); }, [update]);
  const reset = useCallback(() => { setPreviewTheme(null); update({ ...DEFAULTS }); }, [update]);

  const value = {
    prefs, update, applyTheme, reset,
    previewTheme, setPreviewTheme,
    resolvedTheme, theme: getTheme(resolvedTheme),
    seasonalTheme: seasonal,
    hydrateFromAccount,
  };
  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}
